// Partner tools, part three (same API as the app): Invoices, Refund requests, Opening hours, Packages.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock3, FileText, PackagePlus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

const inp = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const gbp = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const day = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
const Head = ({ icon: Icon, title, sub }) => (
  <div className="mb-6 flex items-start gap-3">
    <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" aria-hidden="true" /></span>
    <div><h1 className="text-2xl font-bold tracking-tight">{title}</h1>{sub ? <p className="text-sm text-muted-foreground">{sub}</p> : null}</div>
  </div>
);

// ---------------------------------------------------------------- Invoices
export function PartnerInvoices() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ client_name: "", client_email: "", label: "", amount: "", due_date: "", note: "", send_email: true });
  const load = () => api("/provider/invoices").then(setD).catch(() => setD({ invoices: [], total: 0, count: 0 }));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    const amount = parseFloat(f.amount);
    if (!f.client_name.trim() || !f.label.trim() || !(amount > 0)) { toast.error("Add a client, what it's for and an amount"); return; }
    try {
      await api("/provider/invoices", { method: "POST", body: { client_name: f.client_name.trim(), client_email: f.client_email.trim() || undefined,
        lines: [{ label: f.label.trim(), amount }], note: f.note.trim() || undefined, due_date: f.due_date || undefined, send_email: !!(f.send_email && f.client_email.trim()) } });
      toast.success(f.send_email && f.client_email ? "Invoice created and emailed" : "Invoice created");
      setF({ client_name: "", client_email: "", label: "", amount: "", due_date: "", note: "", send_email: true }); load();
    } catch (err) { toast.error(err.message || "Couldn't create the invoice"); }
  };
  if (!d) return <Skeleton className="h-72 rounded-2xl" />;
  return (
    <div data-testid="partner-invoices">
      <Head icon={FileText} title="Invoices" sub={(d.count || 0) + " invoices · " + gbp(d.total) + " invoiced"} />
      <form onSubmit={create} className="grid gap-3 rounded-2xl border p-4 sm:grid-cols-2" aria-label="New invoice">
        <input className={inp} placeholder="Client name" aria-label="Client name" value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} />
        <input className={inp} placeholder="Client email (to send it)" aria-label="Client email" type="email" value={f.client_email} onChange={(e) => setF({ ...f, client_email: e.target.value })} />
        <input className={inp} placeholder="What it's for" aria-label="Line description" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <input className={inp} placeholder="Amount (£)" aria-label="Amount" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <label className="text-sm">Due date<input className={inp + " mt-1"} type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></label>
        <input className={inp + " self-end"} placeholder="Note (optional)" aria-label="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} /> Email it to the client</label>
        <Button type="submit" className="sm:justify-self-end">Create invoice</Button>
      </form>
      <ul className="mt-6 divide-y rounded-2xl border">
        {(d.invoices || []).length === 0 ? <li className="p-4 text-sm text-muted-foreground">No invoices yet.</li> : d.invoices.map((i) => (
          <li key={i.id} className="flex items-center gap-3 p-4 text-sm">
            <div className="min-w-0 flex-1"><p className="truncate font-semibold">{(i.number ? i.number + " · " : "") + (i.client_name || "Client")}</p>
              <p className="truncate text-xs text-muted-foreground">{[day(i.created_at), i.due_date ? "due " + day(i.due_date) : null, i.status].filter(Boolean).join(" · ")}</p></div>
            <span className="font-semibold">{gbp(i.total ?? i.amount)}</span>
            {i.pdf_url ? <a className="text-xs font-semibold underline" href={i.pdf_url} target="_blank" rel="noreferrer">PDF</a> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------- Refund requests
export function PartnerRefunds() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState({});
  const load = () => api("/provider/refund-requests").then(setD).catch(() => setD({ requests: [], pending: 0 }));
  useEffect(() => { load(); }, []);
  const act = async (r, kind) => {
    const e = edit[r.id] || {};
    try {
      if (kind === "approve") {
        const amt = e.amount !== undefined && e.amount !== "" ? parseFloat(e.amount) : undefined;
        await api("/provider/refund-requests/" + r.id + "/approve", { method: "POST", body: { amount: amt, note: e.note || undefined } });
        toast.success("Refund approved");
      } else {
        await api("/provider/refund-requests/" + r.id + "/decline", { method: "POST", body: { note: e.note || undefined } });
        toast.success("Request declined");
      }
      load();
    } catch (err) { toast.error(err.message || "Couldn't update the request"); }
  };
  if (!d) return <Skeleton className="h-60 rounded-2xl" />;
  return (
    <div data-testid="partner-refunds">
      <Head icon={RotateCcw} title="Refund requests" sub={(d.pending || 0) + " waiting for you"} />
      <ul className="space-y-3">
        {(d.requests || []).length === 0 ? <li className="rounded-2xl border p-4 text-sm text-muted-foreground">No refund requests.</li> : d.requests.map((r) => (
          <li key={r.id} className="rounded-2xl border p-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold">{(r.customer_name || "Customer") + " · " + (r.listing_title || "Booking")}</p>
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{r.status}</span>
            </div>
            <p className="mt-1 text-muted-foreground">{r.reason}</p>
            <p className="mt-1 text-xs text-muted-foreground">{[day(r.created_at), r.amount ? "paid " + gbp(r.amount) : null].filter(Boolean).join(" · ")}</p>
            {r.status === "pending" ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-[140px_1fr_auto_auto]">
                <input className={inp} placeholder={r.amount ? "Full: " + gbp(r.amount) : "Amount (£)"} aria-label="Refund amount" inputMode="decimal"
                  value={(edit[r.id] || {}).amount || ""} onChange={(e) => setEdit({ ...edit, [r.id]: { ...(edit[r.id] || {}), amount: e.target.value } })} />
                <input className={inp} placeholder="Note to the customer (optional)" aria-label="Note"
                  value={(edit[r.id] || {}).note || ""} onChange={(e) => setEdit({ ...edit, [r.id]: { ...(edit[r.id] || {}), note: e.target.value } })} />
                <Button onClick={() => act(r, "approve")}>Approve</Button>
                <Button variant="outline" onClick={() => act(r, "decline")}>Decline</Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------- Opening hours
const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
export function PartnerHours() {
  const [d, setD] = useState(null);
  const [wh, setWh] = useState({});
  const [slot, setSlot] = useState(60);
  const [cap, setCap] = useState("");
  const [off, setOff] = useState([]);
  const [offDay, setOffDay] = useState("");
  useEffect(() => {
    api("/provider/hours").then((r) => { setD(r); setWh(r.working_hours || {}); setSlot(r.slot_minutes || 60); setCap(r.daily_cap || ""); setOff(r.time_off || []); }).catch(() => setD({}));
  }, []);
  const keys = Object.keys(wh).length ? Object.keys(wh) : DAYS.map((x) => x[0]);
  const label = (k) => (DAYS.find((x) => x[0] === k) || [k, k.charAt(0).toUpperCase() + k.slice(1)])[1];
  const set = (k, patch) => setWh({ ...wh, [k]: { closed: false, open: "09:00", close: "17:00", ...(wh[k] || {}), ...patch } });
  const save = async () => {
    try { await api("/provider/hours", { method: "PUT", body: { working_hours: wh, time_off: off, slot_minutes: Number(slot) || 60, slot_mode: d.slot_mode, daily_cap: Number(cap) > 0 ? Number(cap) : null } }); toast.success("Hours saved"); }
    catch (err) { toast.error(err.message || "Couldn't save"); }
  };
  if (!d) return <Skeleton className="h-72 rounded-2xl" />;
  return (
    <div data-testid="partner-hours">
      <Head icon={Clock3} title="Opening hours" sub="Customers can only book inside these hours." />
      <div className="divide-y rounded-2xl border">
        {keys.map((k) => { const h = wh[k] || { closed: true }; return (
          <div key={k} className="flex flex-wrap items-center gap-3 p-3 text-sm">
            <span className="w-28 font-semibold">{label(k)}</span>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!h.closed} onChange={(e) => set(k, { closed: !e.target.checked })} /> Open</label>
            {!h.closed ? (<>
              <input className={inp + " w-32"} type="time" aria-label={label(k) + " opens"} value={h.open || "09:00"} onChange={(e) => set(k, { open: e.target.value })} />
              <span>to</span>
              <input className={inp + " w-32"} type="time" aria-label={label(k) + " closes"} value={h.close || "17:00"} onChange={(e) => set(k, { close: e.target.value })} />
            </>) : <span className="text-muted-foreground">Closed</span>}
          </div>
        ); })}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">Appointment length (minutes)<input className={inp + " mt-1"} type="number" min="5" step="5" value={slot} onChange={(e) => setSlot(e.target.value)} /></label>
        <label className="text-sm">Most bookings per day (blank = no limit)<input className={inp + " mt-1"} type="number" min="0" value={cap} onChange={(e) => setCap(e.target.value)} /></label>
      </div>
      <div className="mt-6 rounded-2xl border p-4">
        <p className="font-semibold">Days off</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {off.map((o, i) => { const v = typeof o === "string" ? o : (o.date || o.start || JSON.stringify(o)); return (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs">{v}
              <button type="button" aria-label={"Remove " + v} onClick={() => setOff(off.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3" /></button></span>
          ); })}
          {!off.length ? <span className="text-sm text-muted-foreground">None booked.</span> : null}
        </div>
        <div className="mt-3 flex gap-2">
          <input className={inp + " w-48"} type="date" aria-label="Add a day off" value={offDay} onChange={(e) => setOffDay(e.target.value)} />
          <Button variant="outline" onClick={() => { if (offDay) { setOff([...off, (off.length && typeof off[0] !== "string") ? { date: offDay } : offDay]); setOffDay(""); } }}>Add day off</Button>
        </div>
      </div>
      <Button className="mt-6" onClick={save}>Save hours</Button>
    </div>
  );
}

// ---------------------------------------------------------------- Packages
export function PartnerPackages() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ name: "", service_name: "", sessions: "5", price: "" });
  const load = () => api("/provider/packages").then((r) => setList(Array.isArray(r) ? r : r.packages || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !(parseInt(f.sessions) > 0) || !(parseFloat(f.price) > 0)) { toast.error("Add a name, sessions and a price"); return; }
    try { await api("/provider/packages", { method: "POST", body: { name: f.name.trim(), service_name: f.service_name.trim() || f.name.trim(), sessions: parseInt(f.sessions), price: parseFloat(f.price) } }); toast.success("Package created"); setF({ name: "", service_name: "", sessions: "5", price: "" }); load(); }
    catch (err) { toast.error(err.message || "Couldn't create"); }
  };
  const del = async (p) => { try { await api("/provider/packages/" + p.id, { method: "DELETE" }); load(); } catch (err) { toast.error(err.message || "Couldn't remove"); } };
  if (!list) return <Skeleton className="h-60 rounded-2xl" />;
  return (
    <div data-testid="partner-packages">
      <Head icon={PackagePlus} title="Packages" sub="Sell bundles of sessions up front, e.g. 5 lessons for the price of 4." />
      <form onSubmit={add} className="grid gap-3 rounded-2xl border p-4 sm:grid-cols-[1fr_1fr_110px_120px_auto]" aria-label="New package">
        <input className={inp} placeholder="Package name" aria-label="Package name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input className={inp} placeholder="Service (e.g. Driving lesson)" aria-label="Service" value={f.service_name} onChange={(e) => setF({ ...f, service_name: e.target.value })} />
        <input className={inp} placeholder="Sessions" aria-label="Sessions" type="number" min="1" value={f.sessions} onChange={(e) => setF({ ...f, sessions: e.target.value })} />
        <input className={inp} placeholder="Price (£)" aria-label="Price" inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
        <Button type="submit">Create</Button>
      </form>
      <ul className="mt-6 divide-y rounded-2xl border">
        {list.length === 0 ? <li className="p-4 text-sm text-muted-foreground">No packages yet.</li> : list.map((p) => (
          <li key={p.id} className="flex items-center gap-3 p-4 text-sm">
            <div className="min-w-0 flex-1"><p className="truncate font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{(p.sessions || "?") + " × " + (p.service_name || "session")}</p></div>
            <span className="font-semibold">{gbp(p.price)}</span>
            <Button size="icon" variant="ghost" aria-label={"Delete " + p.name} onClick={() => del(p)}><Trash2 className="h-4 w-4" /></Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
